import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import * as L from "lucide-react";

/** Promises the user made to other people. Reminders are nudges the user asked
 *  for; a promise has a face attached, so it reads differently and ends in a
 *  kept / slipped state rather than just "done". */
export function PromisesPanel({ promises, onChange }) {
  const [busy, setBusy] = useState(null);
  if (!promises?.length) return null;

  const owe = promises.filter(p => (p.direction || "you_owe") === "you_owe");
  const waiting = promises.filter(p => p.direction === "waiting_on");

  const keep = async (p) => {
    setBusy(p.id);
    try {
      await api.patch(`/commitments/${p.id}/keep`);
      onChange?.();
      toast.success("Kept.");
    } catch { toast.error("Couldn't mark that kept — try again."); }
    finally { setBusy(null); }
  };
  const push = async (p) => {
    setBusy(p.id);
    try {
      await api.patch(`/commitments/${p.id}/push`, { hours: 24 });
      onChange?.();
      toast.success("Pushed to tomorrow.");
    } catch { toast.error("Couldn't push that — try again."); }
    finally { setBusy(null); }
  };

  const group = (label, items) => items.length > 0 && (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.06em] text-[var(--rm-text-faint)] px-1">
        <L.Handshake size={13}/> {label}
      </div>
      {items.map(p => (
        <div key={p.id} className="rm-paper p-5" data-testid={`home-promise-${p.id}`}>
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[15px] leading-snug" data-testid={`home-promise-title-${p.id}`}>
                {p.title}
              </div>
              <div className="mt-1.5 flex items-center gap-2 text-[11px] font-mono text-[var(--rm-text-faint)]">
                {p.counterparty?.name && (
                  <span data-testid={`home-promise-who-${p.id}`}>
                    {p.direction === "waiting_on" ? `from ${p.counterparty.name}` : `for ${p.counterparty.name}`}
                  </span>
                )}
                <span data-testid={`home-promise-due-${p.id}`}>{dueLabel(p)}</span>
                {p.state === "slipped" && (
                  <span className="text-[#b45309]" data-testid={`home-promise-slipped-${p.id}`}>
                    slipped
                  </span>
                )}
              </div>
              {p.note && (
                <div className="mt-1.5 text-[13px] text-[var(--rm-text-muted)] leading-relaxed">{p.note}</div>
              )}
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => keep(p)}
              disabled={busy === p.id}
              data-testid={`home-promise-keep-${p.id}`}
              className="rounded-full px-3.5 py-1.5 text-[12px] font-medium bg-[var(--rm-ink-900)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {p.direction === "waiting_on" ? "They did it" : "I did it"}
            </button>
            <button
              type="button"
              onClick={() => push(p)}
              disabled={busy === p.id}
              data-testid={`home-promise-push-${p.id}`}
              className="rounded-full px-3.5 py-1.5 text-[12px] font-medium border border-[var(--rm-border)] text-[var(--rm-text-muted)] hover:text-[var(--rm-text)] transition-colors disabled:opacity-50"
            >
              Push a day
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="home-promises">
      {group("You said you'd", owe)}
      {group("You're waiting on", waiting)}
    </div>
  );
}

function dueLabel(p) {
  if (!p.due_at) return "";
  const d = new Date(p.due_at);
  const now = new Date();
  const mins = Math.round((d - now) / 60000);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (mins < -60 * 24) return `due ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  if (mins < 0) return "due now";
  if (mins < 60 * 12) return `by ${time}`;
  if (mins < 60 * 36) return `by tomorrow ${time}`;
  return `by ${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`;
}

export default PromisesPanel;
